/**
 * The account screen's "Escopo de análise" control: `GET`/`PUT
 * .../tracked-accounts/{id}/analysis-scope` (`../../adversaries/api/queries.ts`, which owns the
 * ALL/RELATED vocabulary -- `./Account.tsx`'s todos/relacionados toggle on `Publications` is
 * unrelated: that one picks which posts *this screen* displays, this one picks which posts the
 * account's own metrics, comments, likes and comment analysis are computed over.
 *
 * `null` writes "inherit from alignment"; `ALL`/`RELATED` writes an explicit override. The
 * trigger always shows the resolved value (`ALL` or `RELATED`) even for the inherit option, so a
 * collapsed select never hides what scope is actually in effect.
 */

import type { ReactNode } from 'react';

import { QueryStates } from '../../../../chrome/state/QueryStates';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../ui/select';
import { Skeleton } from '../../../../ui/skeleton';
import { useAnalysisScope, useWriteAnalysisScope } from '../../adversaries/api/queries';
import type { AnalysisScope } from '../../adversaries/api/schemas';
import { am } from '../messages';

const INHERIT_VALUE = 'inherit';

function effectiveLabel(effective: AnalysisScope): string {
  return am(`analysisScope.effective.${effective}`);
}

export function AnalysisScopeSelect(props: {
  campaignId: string;
  trackedAccountId: string;
}): ReactNode {
  const query = useAnalysisScope(props.campaignId, props.trackedAccountId);
  const write = useWriteAnalysisScope(props.campaignId, props.trackedAccountId);

  return (
    <QueryStates query={query} loading={<Skeleton className="h-9 w-64" />}>
      {(data) => (
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground" id="analysis-scope-label">
            {am('analysisScope.label')}
          </label>
          <Select
            value={data.analysis_scope ?? INHERIT_VALUE}
            onValueChange={(next) =>
              write.mutate({
                analysis_scope: next === INHERIT_VALUE ? null : (next as AnalysisScope),
              })
            }
            disabled={write.isPending}
          >
            <SelectTrigger aria-labelledby="analysis-scope-label" size="sm" className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={INHERIT_VALUE}>
                {am('analysisScope.inherit', { effective: effectiveLabel(data.effective) })}
              </SelectItem>
              <SelectItem value="ALL">{am('analysisScope.all')}</SelectItem>
              <SelectItem value="RELATED">{am('analysisScope.related')}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{am('analysisScope.hint')}</p>
        </div>
      )}
    </QueryStates>
  );
}
